import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Gallery,
  GalleryMain,
  GalleryItem,
  useGallery,
} from '@wethegit/react-gallery';
import './HomePage.css';
import HomeCard from '../HomeCard/HomeCard';

const ITEMS = [
  {
    id: 1,
    src: 'https://www.lmseducation.in/hero/education-platform-bg.png',
    alt: 'Students learning drone technology on the LMS platform',
    title: 'Learn Drone Technology, Your Way',
    description:
      'From flight fundamentals to advanced UAV systems, our structured courses take you from beginner to industry-ready — anytime, anywhere.',
  },
  {
    id: 2,
    src: 'https://res.cloudinary.com/wy5wgow3/image/upload/v1786598130/ChatGPT_Image_Aug_13_2026_10_45_21_AM.png',
    alt: 'UAV and drone piloting training module',
    title: 'Master UAV Piloting Skills',
    description: 'Hands-on modules, real flight scenarios, and expert-led lessons built for aspiring drone pilots.',
  },
  {
    id: 3,
    src: 'https://res.cloudinary.com/wy5wgow3/image/upload/v1786597950/ChatGPT_Image_Aug_13_2026_10_41_32_AM.png',
    alt: 'Certification and career growth in drone technology',
    title: 'Get Industry-Recognized Certified',
    description:
      'Complete your course, pass the assessment, and earn a certification that opens doors in the growing UAV industry.',
  },
  {
    id: 4,
    src: 'https://res.cloudinary.com/wy5wgow3/image/upload/v1786598814/ChatGPT_Image_Aug_13_2026_10_56_22_AM.png',
    alt: 'Track learning progress on the LMS dashboard',
    title: 'Track Your Progress, Anytime',
    description:
      'Monitor your course completion, quiz scores, and certificates in one simple, easy-to-use dashboard.',
  },
];

const TOTAL = ITEMS.length;
const AUTO_SLIDE_MS = 4000;

const indexOfId = (id) => ITEMS.findIndex((item) => item.id === id);

function Slides({ thumbOrder }) {
  const { goToIndex } = useGallery();

  return (
    <GalleryMain
      className="upskld_home_pg_track"
      renderGalleryItem={({ item, index, active }) => {
        const offset = thumbOrder.indexOf(item.id) - (thumbOrder.length - 1) / 2;

        return (
          <GalleryItem
            key={item.id}
            index={index}
            active={active}
            className="upskld_home_pg_item"
            data-active={String(active)}
          >
            <div className="upskld_home_pg_slide" style={{ '--offset': offset }}>
              <img src={item.src} alt={item.alt} className="upskld_home_pg_image" />
              {!active && (
                <button
                  type="button"
                  className="upskld_home_pg_thumb_btn"
                  tabIndex={-1}
                  onClick={() => goToIndex(index)}
                >
                  <span className="upskld_home_pg_visually_hidden">Show {item.title}</span>
                </button>
              )}
            </div>
          </GalleryItem>
        );
      }}
    />
  );
}

function ActiveContent({ thumbOrder }) {
  const { activeIndex, goToIndex } = useGallery();
  const item = ITEMS[activeIndex];

  const goPrev = () => goToIndex(indexOfId(thumbOrder[thumbOrder.length - 1]));
  const goNext = () => goToIndex(indexOfId(thumbOrder[0]));

  const nextRef = useRef(goNext);
  nextRef.current = goNext;

  useEffect(() => {
    const timer = setInterval(() => {
      nextRef.current();
    }, AUTO_SLIDE_MS);
    return () => clearInterval(timer);
  }, [activeIndex]);

  return (
    <div className="upskld_home_pg_rail">
      <div className="upskld_home_pg_content" key={item.id}>
        <p className="upskld_home_pg_kicker">
          {String(activeIndex + 1).padStart(2, '0')} / {String(TOTAL).padStart(2, '0')}
        </p>
        <h2 className="upskld_home_pg_title">{item.title}</h2>
        <p className="upskld_home_pg_description">{item.description}</p>
      </div>

      <div className="upskld_home_pg_controls">
        <button type="button" className="upskld_home_pg_nav_btn" onClick={goPrev}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          <span className="upskld_home_pg_visually_hidden">Previous</span>
        </button>
        <button type="button" className="upskld_home_pg_nav_btn" onClick={goNext}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
          <span className="upskld_home_pg_visually_hidden">Next</span>
        </button>
      </div>
    </div>
  );
}

const HomePage = () => {
  const [thumbOrder, setThumbOrder] = useState(() =>
    ITEMS.slice(1).map((item) => item.id)
  );

  const handleChange = useCallback(({ oldIndex, newIndex }) => {
    setThumbOrder((order) => [
      ...order.filter((id) => id !== ITEMS[newIndex].id),
      ITEMS[oldIndex].id,
    ]);
  }, []);

  return (
    <>
      <div className="upskld_home_pg_root">
        <Gallery
          items={ITEMS}
          draggable={false}
          className="upskld_home_pg_gallery"
          onChange={handleChange}
        >
          <Slides thumbOrder={thumbOrder} />
          <ActiveContent thumbOrder={thumbOrder} />
        </Gallery>
      </div>
      <HomeCard />
    </>
  );
};

export default HomePage;